<script>
import baseMixin from '@/mixins/baseMixin'
import formMixin from '@/mixins/formMixin'
import { mapActions, mapGetters } from 'vuex'
import PromiseDialog from '@/components/general/promise-dialog.vue'
import PromiseDialogDto from '@/models/dialog/promiseDialogDto'
import PromiseDialogEvent from '@/shared/constants/dialog/PromiseDialogEvent'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'

export default {
  name: 'UnapprovedBookingButtons',
  components: {
    PromiseDialog,
    CustomTooltip,
  },
  mixins: [baseMixin, formMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    reasonForDecline: '',
    isApproved: false,
    isDeclineLoading: false,
  }),
  computed: {
    ...mapGetters({
      isBookingPending: 'bookingApprovals/isBookingPending',
      hasAnyApproving: 'bookingApprovals/hasAnyApproving',
      isLoadingSubmitPendingApproval:
        'bookingApprovals/isLoadingSubmitPendingApproval',
    }),
    isDialogDeclineDisabled() {
      return this.reasonForDecline.trim() === ''
    },
    isApproveLoading() {
      return this.isBookingPending(this.booking.id)
    },
    isApproveDisabled() {
      return this.isLoadingSubmitPendingApproval || this.isApproved
    },
    isDeclineDisabled() {
      return this.isLoadingSubmitPendingApproval || this.hasAnyApproving
    },
    buttonAttrs() {
      return {
        width: this.isMobile ? '50' : '90',
        height: '40',
        borderRadius: '4px',
        fab: this.isMobile,
      }
    },
  },
  watch: {
    isApproveLoading(newVal) {
      if (!newVal && this.isApproved) {
        this.isApproved = false // on failure remove loading and disabled state
      }
    },
  },
  methods: {
    ...mapActions({
      declinePendingApproval: 'bookingApprovals/declinePendingApproval',
      queueApproval: 'bookingApprovals/queueApproval',
    }),
    handleApprove() {
      this.isApproved = true
      this.queueApproval(this.booking.id)
    },
    async handleDecline() {
      this.isDeclineLoading = true

      const { isSuccess } = await this.declinePendingApproval({
        reason: this.reasonForDecline,
        bookingId: this.booking.id,
      })

      if (isSuccess) {
        this.$emit('reloadBookingList')
      }

      this.isDeclineLoading = false
    },
    async openDeclineDialog() {
      this.reasonForDecline = ''

      const promiseDialogConfig = this.getDeclineDialogConfig()

      const result = await this.$refs.declineDialog.show(
        new PromiseDialogDto(promiseDialogConfig)
      )

      this.$nextTick(() => this.$refs.reasonTextarea?.resetValidation())

      if (result === PromiseDialogEvent.OK) await this.handleDecline()
    },
    getDeclineDialogConfig() {
      return {
        title: this.$t(
          'booking.details.approvals.submit.rejectReasonDialogTitle'
        ),
        persistent: true,
        actions: [
          {
            text: this.$t('general.cancel'),
            event: PromiseDialogEvent.Cancel,
            attributes: {
              text: true,
              large: true,
            },
          },
          {
            text: this.$t('customeToolTip.decline'),
            event: PromiseDialogEvent.OK,
            attributes: {
              text: true,
              large: true,
              color: VuetifyColourClass.error,
              disabled: () => this.isDialogDeclineDisabled,
            },
          },
        ],
      }
    },
  },
}
</script>

<template>
  <div class="w-100 d-flex justify-space-around">
    <CustomTooltip :text="$t('customeToolTip.decline')" :disable="!isMobile">
      <BaseButton
        v-bind="buttonAttrs"
        secondary
        :icon="isMobile ? 'mdi-close' : undefined"
        class="grey--text"
        :loading="isDeclineLoading"
        :disabled="isDeclineDisabled"
        @click="openDeclineDialog"
      >
        {{ $t('customeToolTip.decline') }}
      </BaseButton>
    </CustomTooltip>

    <CustomTooltip :text="$t('customeToolTip.approve')" :disable="!isMobile">
      <BaseButton
        v-bind="buttonAttrs"
        primary
        :icon="isMobile || isApproved ? 'mdi-check' : undefined"
        :loading="isApproveLoading"
        :disabled="isApproveDisabled"
        @click="handleApprove"
      >
        {{ $t('customeToolTip.approve') }}
      </BaseButton>
    </CustomTooltip>

    <PromiseDialog ref="declineDialog" width="600">
      <template v-slot:title>
        <div class="text-h6 py-6 d-flex align-center px-4">
          <v-icon
            class="red--text text--lighten-1 mr-2"
            :size="isMobile ? 28 : 32"
            >mdi-bookmark-remove-outline</v-icon
          >
          {{ $t('booking.details.approvals.submit.rejectReasonDialogTitle') }}
        </div>
      </template>
      <template v-slot:text>
        <v-textarea
          ref="reasonTextarea"
          v-model="reasonForDecline"
          outlined
          :label="$t('booking.details.approvals.submit.rejectReasonLabel')"
          required
          auto-grow
          auto-focus
          class="mx-1 px-4"
          :rules="[rules.required]"
        />
      </template>
    </PromiseDialog>
  </div>
</template>
